/**
 * DOM rendering helpers for the Career Advisor chat UI.
 */

/**
 * Append a message bubble to the chat container.
 * @param {HTMLElement} container - The messages container
 * @param {string} role - "user", "assistant", or "system"
 * @param {string} html - Pre-rendered HTML content (from server for assistant, escaped for user)
 */
export function renderMessage(container, role, html) {
  const wrapper = document.createElement("div")
  wrapper.className = messageWrapperClass(role)

  const bubble = document.createElement("div")
  bubble.className = messageBubbleClass(role)

  if (role === "user") {
    bubble.textContent = html
  } else {
    bubble.innerHTML = html
  }

  wrapper.appendChild(bubble)
  container.appendChild(wrapper)
}

/**
 * Render goal selection buttons.
 * @param {HTMLElement} container - The goals container
 * @param {Array} goals - Array of { key, label, description }
 * @param {Function} onSelect - Callback when a goal is clicked
 */
export function renderGoalButtons(container, goals, onSelect) {
  container.innerHTML = ""

  const wrapper = document.createElement("div")
  wrapper.className = "flex flex-wrap gap-2"

  goals.forEach((goal) => {
    const button = document.createElement("button")
    button.type = "button"
    button.className =
      "px-4 py-2 text-sm font-medium rounded-lg border border-cyan-200 " +
      "text-cyan-700 bg-white hover:bg-cyan-50 hover:border-cyan-300 " +
      "transition-colors"
    button.textContent = goal.label
    button.title = goal.description
    button.addEventListener("click", () => onSelect(goal))
    wrapper.appendChild(button)
  })

  container.appendChild(wrapper)
}

/**
 * Render the session summary card.
 * @param {HTMLElement} container - The summary container
 * @param {Object} summary - Summary data from the server
 */
export function renderSummary(container, summary) {
  const card = document.createElement("div")
  card.className =
    "bg-white rounded-xl shadow-sm border border-gray-200 p-6"

  if (summary.text) {
    const content = document.createElement("div")
    content.className = "prose prose-sm max-w-none"
    content.innerHTML = summary.text
    card.appendChild(content)
  }

  const actions = document.createElement("div")
  actions.className = "mt-6 flex flex-wrap gap-3"

  const newSessionBtn = document.createElement("a")
  newSessionBtn.href = window.location.pathname
  newSessionBtn.className =
    "inline-flex items-center px-4 py-2 bg-cyan-700 text-white text-sm " +
    "font-medium rounded-lg hover:bg-cyan-800 transition-colors no-underline"
  newSessionBtn.textContent = "Start New Session"
  actions.appendChild(newSessionBtn)

  const calcLink = document.createElement("a")
  calcLink.href = "/tax-calculator/"
  calcLink.className =
    "inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 " +
    "text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors no-underline"
  calcLink.textContent = "Try our Calculators"
  actions.appendChild(calcLink)

  card.appendChild(actions)
  container.appendChild(card)
}

/**
 * Show the typing indicator.
 * @param {HTMLElement} indicator - The typing indicator element
 */
export function showTypingIndicator(indicator) {
  indicator.classList.remove("hidden")
}

/**
 * Hide the typing indicator.
 * @param {HTMLElement} indicator - The typing indicator element
 */
export function hideTypingIndicator(indicator) {
  indicator.classList.add("hidden")
}

/**
 * Create an empty assistant message bubble for streaming.
 * @param {HTMLElement} container - The messages container
 * @returns {HTMLElement} The bubble element to append text to
 */
export function createStreamingBubble(container) {
  const wrapper = document.createElement("div")
  wrapper.className = messageWrapperClass("assistant")

  const bubble = document.createElement("div")
  bubble.className = messageBubbleClass("assistant")
  bubble.dataset.streaming = "true"

  // Start with blinking cursor
  const cursor = document.createElement("span")
  cursor.className = "streaming-cursor inline-block w-0.5 h-4 bg-gray-400 animate-pulse align-text-bottom"
  bubble.appendChild(cursor)

  wrapper.appendChild(bubble)
  container.appendChild(wrapper)

  return bubble
}

/**
 * Append streamed text to a streaming bubble.
 * @param {HTMLElement} bubble - The streaming bubble element
 * @param {string} text - The text chunk to append
 */
export function appendStreamDelta(bubble, text) {
  // Remove cursor if present
  const cursor = bubble.querySelector(".streaming-cursor")

  // Get or create text node
  let textNode = bubble.querySelector(".stream-text")
  if (!textNode) {
    textNode = document.createElement("span")
    textNode.className = "stream-text whitespace-pre-wrap"
    bubble.insertBefore(textNode, cursor)
  }

  textNode.textContent += text

  // Re-append cursor at the end
  if (cursor) {
    bubble.appendChild(cursor)
  }
}

/**
 * Finalize a streaming bubble: replace plain text with rendered HTML.
 * @param {HTMLElement} bubble - The streaming bubble element
 * @param {string} html - The final rendered HTML
 */
export function finalizeStreamBubble(bubble, html) {
  delete bubble.dataset.streaming

  // Remove cursor
  const cursor = bubble.querySelector(".streaming-cursor")
  if (cursor) cursor.remove()

  // Replace stream text with final HTML
  bubble.innerHTML = html
}

/**
 * Insert a tool result card before the streaming bubble.
 * @param {HTMLElement} container - The messages container
 * @param {HTMLElement} bubble - The streaming bubble
 * @param {HTMLElement} cardElement - The card to insert
 */
export function insertToolCard(container, bubble, cardElement) {
  const wrapper = bubble.parentElement
  container.insertBefore(cardElement, wrapper)
}

/**
 * Update the typing indicator text.
 * @param {HTMLElement} textElement - The text span inside the typing indicator
 * @param {string} message - The new status message
 */
export function updateTypingText(textElement, message) {
  if (textElement) {
    textElement.textContent = message
  }
}

// Private helpers

function messageWrapperClass(role) {
  const base = "flex"
  if (role === "user") return `${base} justify-end`
  return base
}

function messageBubbleClass(role) {
  const base = "max-w-[85%] rounded-xl px-4 py-3 text-sm"
  switch (role) {
    case "user":
      return `${base} bg-cyan-700 text-white`
    case "assistant":
      return `${base} bg-gray-100 text-gray-900 prose prose-sm max-w-none`
    case "system":
      return `${base} bg-amber-50 text-amber-800 border border-amber-200`
    default:
      return `${base} bg-gray-100 text-gray-900`
  }
}
